'use client';

import { useMemo } from 'react';
import { SectionContainer, MaskedText, SectionHeader, Button } from '@/components/ui';
import { IconCheckCircle, IconAlertTriangle, IconAlertCircle } from '@repo/svgs';
import { ModalTrigger } from '@/components/ui/ModalTrigger';
import { cn } from '@/lib/cn';
import { useAnalytics } from '@/hooks/useAnalytics';
import { RodentHistoryContent } from './RodentHistoryModal';
import { getRodentTitleAndDescription } from '@/app/building/context/constants';

interface RodentInfestationSectionProps {
  buildingId: string;
  buildingSlug: string;
  buildingAddress: string;
  isBlurRequired?: boolean;
  baseRodentData?: any | null;
  baseLoading?: boolean;
  baseError?: string | null;
}

// Format date to "MMM DD, YYYY" format
const formatDate = (dateString?: string): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return dateString;
  }
};

export function RodentInfestationSection({
  buildingId,
  buildingSlug,
  buildingAddress,
  isBlurRequired = false,
  baseRodentData,
  baseLoading = false,
  baseError,
}: RodentInfestationSectionProps) {
  const analytics = useAnalytics();
  // Transform API response to component format
  const { latestEntry, allEntries } = useMemo(() => {
    if (!baseRodentData) {
      return { latestEntry: null, allEntries: [] };
    }

    // Handle different API response formats - use first dataset
    const rodents = baseRodentData.rodents || baseRodentData.rodent || baseRodentData;
    const rodentsArray = Array.isArray(rodents) ? rodents : [];

    // Get the first filing (most recent)
    const latestFiling = rodentsArray.length > 0 ? rodentsArray[0] : null;

    if (!latestFiling) {
      return { latestEntry: null, allEntries: [] };
    }

    // Get result field - handle both camelCase and snake_case
    const result = latestFiling.result || '';
    const inspectionDate =
      latestFiling.inspectionDate ||
      latestFiling.inspection_date ||
      latestFiling.approvedDate ||
      latestFiling.approved_date ||
      latestFiling.filingDate ||
      latestFiling.filing_date ||
      latestFiling.date;
    const inspectionType = latestFiling.inspectionType || latestFiling.inspection_type || '';

    // Transform all entries for modal - convert snake_case to camelCase
    const transformedEntries = rodentsArray.map((entry: any) => ({
      inspectionDate:
        entry.inspectionDate ||
        entry.inspection_date ||
        entry.approvedDate ||
        entry.approved_date ||
        entry.filingDate ||
        entry.filing_date ||
        entry.date,
      approvedDate:
        entry.approvedDate ||
        entry.approved_date ||
        entry.inspectionDate ||
        entry.inspection_date ||
        '',
      result: entry.result || '',
      inspectionType: entry.inspectionType || entry.inspection_type || '',
    }));

    return {
      latestEntry: {
        result,
        inspectionDate,
        inspectionType,
        hasData: true,
      },
      allEntries: transformedEntries,
    };
  }, [baseRodentData]);

  // Determine variant based on result (not from API response)
  const getVariantFromResult = (resultValue: string): 'success' | 'alert' | 'caution' => {
    if (resultValue === 'Passed') {
      return 'success';
    } else if (resultValue === 'Rat Activity' || resultValue === 'Failed for Other R') {
      return 'alert';
    } else {
      // Bait applied, Monitoring visit, or any other result
      return 'caution';
    }
  };

  // No data case
  if (!latestEntry?.hasData || baseError) {
    return (
      <div>
        <SectionHeader
          title={`Rodent infestation history for ${buildingAddress}`}
          subtitleText="City data from past 6 months"
        />
        <div className="md:px-4 px-2">
          {isBlurRequired ? (
            <div className="bg-secondary rounded-xl p-4 mt-2">
              <MaskedText className="h-5 w-full !mb-0" />
            </div>
          ) : (
            <p className="text-body-2 text-primary">
              This building was not inspected for rodent activity in the past 6 months.
            </p>
          )}
        </div>
      </div>
    );
  }

  // Provide fallback values for testing when latestEntry is null
  const { result = '', inspectionDate } = latestEntry || {};
  const variant = getVariantFromResult(result);
  const formattedDate = formatDate(inspectionDate);

  // Get message based on result for section display
  const getMessage = () => {
    if (result === 'Passed') {
      return (
        <div>
          <div>This building passed the last rodent inspection on {formattedDate}.</div>
        </div>
      );
    } else if (result === 'Rat Activity') {
      return (
        <div>
          <div>
            This building had reported rodent activity at the last rodent inspection on{' '}
            {formattedDate}.
          </div>
        </div>
      );
    } else if (result === 'Failed for Other R') {
      return (
        <div>
          <div>
            This building failed the last rodent inspection due to exposed garbage, harborage
            conditions or mice on {formattedDate}.
          </div>
        </div>
      );
    } else if (result === 'Bait applied') {
      return (
        <div>
          <div>
            Baits were applied on {formattedDate}. This building may require further monitoring.
          </div>
        </div>
      );
    } else if (result === 'Monitoring visit') {
      return (
        <div>
          <div>
            Follow-up visits from the Health Department to monitor progress from the last failed
            inspections on {formattedDate}.
          </div>
        </div>
      );
    } else {
      return <div>This building's rodent inspection history is not available.</div>;
    }
  };

  // Get background and text colors based on variant
  const getVariantStyles = () => {
    switch (variant) {
      case 'success':
        return {
          bg: 'bg-success-low',
          text: 'text-success',
          icon: IconCheckCircle,
        };
      case 'alert':
        return {
          bg: 'bg-alert-low',
          text: 'text-alert',
          icon: IconAlertCircle,
        };
      case 'caution':
        return {
          bg: 'bg-caution-low',
          text: 'text-caution',
          icon: IconAlertCircle,
        };
      default:
        return {
          bg: 'bg-secondary',
          text: 'text-primary',
          icon: IconAlertCircle,
        };
    }
  };

  const variantStyles = getVariantStyles();
  const IconComponent = variantStyles.icon;

  // Modal content function - just return the content, not the Modal wrapper
  const getRodentHistoryContent = () => (
    <RodentHistoryContent rodentData={allEntries} isBlurRequired={isBlurRequired} />
  );

  return (
    <div>
      <SectionHeader
        title={`Rodent infestation history for ${buildingAddress}`}
        subtitleText="City data from past 3 years"
      />
      <div
        className={cn(
          'flex items-start gap-2 px-4 py-2 rounded-xl mt-2 md:mx-4 mx-2',
          variantStyles.bg,
        )}
      >
        <IconComponent className={cn('w-4 h-4 shrink-0 mt-0.5', variantStyles.text)} />
        <div className="flex-1">
          {isBlurRequired ? (
            <MaskedText className="h-5 w-full" />
          ) : (
            <div className={cn('text-body-2', variantStyles.text)}>{getMessage()}</div>
          )}
        </div>
      </div>

      {/* View full history button */}
      {latestEntry?.hasData && (
        <ModalTrigger
          label="Rodent infestation history"
          content={getRodentHistoryContent}
          maxWidth="md"
          onOpen={() => analytics.track('rentalreport_rodent_historyview')}
          onClose={() => analytics.track('rentalreport_rodent_historyview_exit')}
        >
          <Button
            variant="secondary"
            className="md:w-fit mt-4 md:mx-4 mx-2"
            disabled={isBlurRequired}
          >
            View full history
          </Button>
        </ModalTrigger>
      )}
    </div>
  );
}
